import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { DocumentInfoVariant, ICitation, getDocumentInfo } from "@/api";
import {
    DOCUMENT_INFO_STALE_TIME_MS,
    documentInfoOptions,
    documentInfoQueryKey,
    documentInfoQueryOptions,
} from "@/api/documents";
import { isAxiosNotFoundError } from "@/api/errors";
import { useAuiState } from "@assistant-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { DocumentViewerContext } from "./DocumentViewerContext";
import {
    DocumentViewerOpenContext,
    DocumentViewerOpenOutcome,
    DocumentViewerState,
    DocumentViewerTab,
    isOutputDocumentOrigin,
    isSpreadsheet,
    nextActiveTabId,
    projectActiveTab,
} from "./DocumentViewerContext.types";

const INITIAL_STATE: DocumentViewerState = { tabs: [], activeTabId: null };
const MAX_TABS = 6;

interface PendingDocumentLoad {
    documentId: number;
    variant?: DocumentInfoVariant;
    slotId: string;
    previous: DocumentViewerState;
    evicted: DocumentViewerTab | undefined;
    restore: () => void;
}

type DocumentLoadRequest = {
    documentId: number;
    variant?: DocumentInfoVariant;
    page?: number;
    context?: Omit<DocumentViewerOpenContext, "variant">;
    source?: ICitation;
    slotId?: string;
} & (
    | { mode?: "open" }
    | {
          mode: "activate";
          openedWith: DocumentViewerTab["openedWith"];
      }
);

type DocumentIdentity = Pick<DocumentViewerTab, "documentId" | "variant">;

interface HeldPdf extends DocumentIdentity {
    url: string;
}

const matchesDocumentRequest = (
    tab: DocumentIdentity,
    request: DocumentIdentity
): boolean =>
    tab.documentId === request.documentId && tab.variant === request.variant;

export const DocumentViewerProvider: React.FC<{
    children: React.ReactNode;
    conversationId?: string;
}> = ({ children, conversationId }) => {
    const [state, setState] = useState<DocumentViewerState>(INITIAL_STATE);
    const queryClient = useQueryClient();
    const stateRef = useRef(state);
    const mountedRef = useRef(true);
    const nextTabIdRef = useRef(0);
    const nextOpenRequestIdRef = useRef(0);
    const jumpToPageRef = useRef<((page: number) => void) | null>(null);
    const fetchCounterRef = useRef(0);
    const pendingLoadRef = useRef<PendingDocumentLoad | null>(null);
    const heldPdfsRef = useRef(new Map<string, HeldPdf>());
    const renderedPendingLoadRef = useRef<PendingDocumentLoad | null>(null);
    const pendingJumpRef = useRef<{ tabId: string; page: number } | null>(null);
    const jumpSeqRef = useRef(0);
    const [jumpSeq, setJumpSeq] = useState(0);

    useLayoutEffect(() => {
        mountedRef.current = true;
        const held = heldPdfsRef.current;
        return () => {
            mountedRef.current = false;
            held.forEach(({ url }) => URL.revokeObjectURL(url));
            held.clear();
        };
    }, []);

    useLayoutEffect(() => {
        renderedPendingLoadRef.current = pendingLoadRef.current;
        const pending = pendingLoadRef.current;
        const returnable = [
            ...stateRef.current.tabs,
            ...(pending?.previous.tabs.filter(
                (tab) => tab.id === pending.slotId
            ) ?? []),
            ...(pending?.evicted ? [pending.evicted] : []),
        ];
        heldPdfsRef.current.forEach((held, id) => {
            if (
                returnable.some(
                    (tab) => tab.id === id && matchesDocumentRequest(tab, held)
                )
            )
                return;
            URL.revokeObjectURL(held.url);
            heldPdfsRef.current.delete(id);
        });
    }, [state]);

    const commit = useCallback((next: DocumentViewerState) => {
        stateRef.current = next;
        setState(next);
    }, []);

    const queueJump = useCallback((tabId: string, page: number) => {
        pendingJumpRef.current = { tabId, page: page - 1 };
        setJumpSeq(++jumpSeqRef.current);
    }, []);

    const mainThreadId = useAuiState((s) => s.threads.mainThreadId);
    const mainThreadIsEstablished = useAuiState((s) => {
        const mainId = s.threads.mainThreadId;
        return (
            s.threads.threadItems.find((t) => t.id === mainId)?.remoteId != null
        );
    });

    const loadDocument = useCallback(
        async function load(
            request: DocumentLoadRequest
        ): Promise<DocumentViewerOpenOutcome> {
            const { documentId, variant, page, context, source } = request;
            const openMode = request.mode ?? "open";
            const activating = openMode === "activate";
            if (!mountedRef.current || context?.signal?.aborted) {
                return { type: "superseded" };
            }
            if (variant && context?.fallbackToDefault) {
                const known = queryClient.getQueryState(
                    documentInfoQueryKey(documentId, { variant })
                );
                if (
                    known?.status === "error" &&
                    isAxiosNotFoundError(known.error) &&
                    Date.now() - known.errorUpdatedAt <
                        DOCUMENT_INFO_STALE_TIME_MS
                ) {
                    return load({ ...request, variant: undefined });
                }
            }

            const pendingActive = stateRef.current.tabs.find(
                (tab) => tab.id === stateRef.current.activeTabId
            );
            if (context?.signal && pendingActive?.status === "loading") {
                return { type: "superseded" };
            }

            const pending = pendingLoadRef.current;
            const replacesPendingSlot =
                !activating &&
                context?.target !== "new-tab" &&
                !stateRef.current.tabs.some((tab) =>
                    matchesDocumentRequest(tab, request)
                ) &&
                !pending?.previous.tabs.some((tab) =>
                    matchesDocumentRequest(tab, request)
                );
            const continuing =
                pending &&
                (matchesDocumentRequest(pending, request) ||
                    replacesPendingSlot)
                    ? pending
                    : null;
            if (pending && !continuing) {
                pending.restore();
            }
            const current = stateRef.current;
            const active = current.tabs.find(
                (tab) => tab.id === current.activeTabId
            );
            const previous = continuing?.previous ?? current;
            const existing = current.tabs.find((tab) =>
                matchesDocumentRequest(tab, request)
            );
            const sameActive = existing?.id === pendingActive?.id;
            const canReuseActive =
                !pending || renderedPendingLoadRef.current !== pending;
            const requestedPage =
                page ?? (existing ? existing.currentPage : undefined);
            const openedWith =
                request.mode === "activate"
                    ? request.openedWith
                    : {
                          ...(context?.origin
                              ? { origin: context.origin }
                              : {}),
                          requestId: ++nextOpenRequestIdRef.current,
                      };
            const outputDownload = isOutputDocumentOrigin(openedWith.origin)
                ? { documentId }
                : null;
            const thisCall = ++fetchCounterRef.current;
            pendingJumpRef.current = null;

            const held = existing && heldPdfsRef.current.get(existing.id);
            if (
                activating &&
                existing?.status === "ready" &&
                existing.selectedSource &&
                held &&
                matchesDocumentRequest(existing, held)
            ) {
                const shown: DocumentViewerTab = {
                    ...existing,
                    openedWith,
                    openMode,
                    initialPage: requestedPage,
                    selectedSource: {
                        ...existing.selectedSource,
                        document_url: held.url,
                    },
                    outputDownload,
                };
                commit({
                    tabs: current.tabs.map((tab) =>
                        tab.id === shown.id ? shown : tab
                    ),
                    activeTabId: shown.id,
                });
                pendingLoadRef.current = null;
                return { type: "opened" };
            }

            if (
                !activating &&
                canReuseActive &&
                sameActive &&
                existing &&
                (existing.status === "ready" ||
                    (!source && existing.status === "unsupported"))
            ) {
                const updated = {
                    ...existing,
                    openedWith,
                    openMode,
                    initialPage: page ?? existing.initialPage,
                    currentPage: page ?? existing.currentPage,
                    selectedSource:
                        source && existing.selectedSource
                            ? {
                                  ...source,
                                  document_url:
                                      existing.selectedSource.document_url,
                                  document_name:
                                      existing.selectedSource.document_name,
                              }
                            : existing.selectedSource,
                    outputDownload:
                        existing.status === "ready" ? outputDownload : null,
                };
                commit({
                    tabs: current.tabs.map((tab) =>
                        tab.id === updated.id ? updated : tab
                    ),
                    activeTabId: updated.id,
                });
                if (page !== undefined && existing.status === "ready") {
                    queueJump(updated.id, page);
                }
                pendingLoadRef.current = null;
                return { type: "opened" };
            }

            const id =
                existing?.id ??
                request.slotId ??
                (context?.target !== "new-tab" ? active?.id : undefined) ??
                `viewer-tab-${++nextTabIdRef.current}`;
            const loading: DocumentViewerTab = {
                id,
                documentId,
                ...documentInfoOptions(variant),
                openedWith,
                openMode,
                fileType: existing?.fileType ?? null,
                selectedSource:
                    existing?.selectedSource ??
                    source ??
                    (id === active?.id ? active.selectedSource : null) ??
                    null,
                status: "loading",
                initialPage: requestedPage,
                currentPage: requestedPage ?? 1,
                outputDownload: null,
            };
            let tabs = current.tabs;
            let evicted = continuing?.evicted;
            if (tabs.some((tab) => tab.id === id)) {
                tabs = tabs.map((tab) => (tab.id === id ? loading : tab));
            } else {
                if (tabs.length >= MAX_TABS) {
                    evicted = tabs.find(
                        (tab) => tab.id !== current.activeTabId
                    );
                    tabs = tabs.filter((tab) => tab.id !== evicted?.id);
                }
                tabs = [...tabs, loading];
            }
            commit({ tabs, activeTabId: id });

            const restorePreviousState = ({
                refreshFallback = false,
                keepNewTab = false,
            } = {}) => {
                if (fetchCounterRef.current !== thisCall) return;
                fetchCounterRef.current++;
                pendingJumpRef.current = null;
                pendingLoadRef.current = null;
                const previousTab = previous.tabs.find((tab) => tab.id === id);
                const retainingNewTab =
                    keepNewTab &&
                    !previousTab &&
                    stateRef.current.tabs.some((tab) => tab.id === id);
                let restored = stateRef.current.tabs.flatMap((tab) => {
                    if (tab.id !== id || retainingNewTab) return [tab];
                    return previousTab ? [previousTab] : [];
                });
                if (evicted && !retainingNewTab) {
                    const previousIndex = previous.tabs.findIndex(
                        (tab) => tab.id === evicted.id
                    );
                    const preceding = previous.tabs.slice(0, previousIndex);
                    const index = restored.filter((tab) =>
                        preceding.some((previous) => previous.id === tab.id)
                    ).length;
                    restored = [
                        ...restored.slice(0, index),
                        evicted,
                        ...restored.slice(index),
                    ];
                }
                const previousActive = restored.find(
                    (tab) => tab.id === previous.activeTabId
                );
                const restoredActive =
                    previousActive ?? restored[restored.length - 1];
                const shouldRefresh =
                    refreshFallback && !previousActive && !!restoredActive;
                commit({
                    tabs: restored.map((tab) =>
                        tab.id === restoredActive?.id &&
                        (tab.initialPage ?? 1) !== tab.currentPage
                            ? { ...tab, initialPage: tab.currentPage }
                            : tab
                    ),
                    activeTabId: shouldRefresh
                        ? null
                        : (restoredActive?.id ?? null),
                });
                if (shouldRefresh) {
                    void load({
                        documentId: restoredActive.documentId,
                        variant: restoredActive.variant,
                        page: restoredActive.currentPage,
                        openedWith: restoredActive.openedWith,
                        mode: "activate",
                    });
                }
            };
            pendingLoadRef.current = {
                documentId,
                variant,
                slotId: id,
                previous,
                evicted,
                restore: () => restorePreviousState({ keepNewTab: true }),
            };
            const handleAbort = () =>
                restorePreviousState({ refreshFallback: true });
            context?.signal?.addEventListener("abort", handleAbort, {
                once: true,
            });

            try {
                const infoOptions = documentInfoOptions(variant);
                const origin = context?.origin;
                const docInfo =
                    !activating && origin
                        ? await queryClient.fetchQuery({
                              ...documentInfoQueryOptions(
                                  documentId,
                                  infoOptions
                              ),
                              queryFn: () =>
                                  getDocumentInfo(documentId, infoOptions),
                              ...(existing && isOutputDocumentOrigin(origin)
                                  ? { staleTime: 0 }
                                  : {}),
                          })
                        : await getDocumentInfo(documentId, infoOptions);
                if (
                    !mountedRef.current ||
                    context?.signal?.aborted ||
                    fetchCounterRef.current !== thisCall
                ) {
                    return { type: "superseded" };
                }
                const spreadsheet = isSpreadsheet(docInfo.file_type ?? null);
                const citation: ICitation = source ??
                    (existing?.selectedSource?.document_id === documentId
                        ? existing.selectedSource
                        : null) ?? {
                        document_id: documentId,
                        document_name: "",
                        document_url: "",
                        pages: page ? String(page) : "",
                        highlight_areas: [],
                        scopes: [],
                    };
                const ready: DocumentViewerTab = {
                    ...loading,
                    fileType: docInfo.file_type ?? null,
                    selectedSource: {
                        ...citation,
                        document_url: docInfo.presigned_url ?? "",
                        document_name:
                            citation.document_name || docInfo.document_name,
                    },
                    status: spreadsheet ? "unsupported" : "ready",
                    outputDownload: spreadsheet ? null : outputDownload,
                };
                const latest = stateRef.current;
                commit({
                    ...latest,
                    tabs: latest.tabs.map((tab) =>
                        tab.id === id ? ready : tab
                    ),
                });
                pendingLoadRef.current = null;
                return { type: "opened" };
            } catch (error) {
                if (
                    !mountedRef.current ||
                    context?.signal?.aborted ||
                    fetchCounterRef.current !== thisCall
                ) {
                    return { type: "superseded" };
                }
                if (
                    variant &&
                    context?.fallbackToDefault &&
                    isAxiosNotFoundError(error)
                ) {
                    restorePreviousState();
                    return load({ ...request, variant: undefined, slotId: id });
                }
                const latest = stateRef.current;
                commit({
                    ...latest,
                    tabs: latest.tabs.map((tab) =>
                        tab.id === id
                            ? {
                                  ...tab,
                                  selectedSource:
                                      tab.selectedSource?.document_id ===
                                      documentId
                                          ? tab.selectedSource
                                          : null,
                                  status: "error",
                                  initialPage: undefined,
                                  outputDownload: null,
                              }
                            : tab
                    ),
                });
                pendingLoadRef.current = null;
                return isAxiosNotFoundError(error)
                    ? { type: "not-found" }
                    : { type: "error" };
            } finally {
                context?.signal?.removeEventListener("abort", handleAbort);
            }
        },
        [commit, queryClient, queueJump]
    );

    const openDocument = useCallback(
        async (source: ICitation, page?: number) => {
            await loadDocument({
                documentId: source.document_id,
                page,
                source,
            });
        },
        [loadDocument]
    );

    const openDocumentById = useCallback(
        (
            documentId: number,
            page?: number,
            { variant, ...context }: DocumentViewerOpenContext = {}
        ) =>
            loadDocument({
                documentId,
                variant,
                page,
                context,
            }),
        [loadDocument]
    );

    const activateTab = useCallback(
        async (id: string) => {
            const current = stateRef.current;
            const tab = current.tabs.find((item) => item.id === id);
            if (!tab || id === current.activeTabId) return;
            await loadDocument({
                documentId: tab.documentId,
                variant: tab.variant,
                page: tab.currentPage,
                openedWith: tab.openedWith,
                mode: "activate",
            });
        },
        [loadDocument]
    );

    const closeDocument = useCallback(() => {
        fetchCounterRef.current++;
        pendingJumpRef.current = null;
        pendingLoadRef.current = null;
        commit(INITIAL_STATE);
    }, [commit]);

    const closeTab = useCallback(
        (id: string) => {
            const current = stateRef.current;
            const index = current.tabs.findIndex((tab) => tab.id === id);
            if (index === -1) return current.activeTabId;
            const nextId = nextActiveTabId(
                current.tabs,
                current.activeTabId,
                id
            );
            const tabs = current.tabs.filter((tab) => tab.id !== id);
            if (tabs.length === 0) {
                closeDocument();
            } else if (current.activeTabId !== id) {
                commit({ ...current, tabs });
            } else {
                commit({ tabs, activeTabId: null });
                if (nextId) void activateTab(nextId);
            }
            return stateRef.current.activeTabId;
        },
        [activateTab, closeDocument, commit]
    );

    const holdTabPdf = useCallback(
        (
            id: string,
            documentUrl: string,
            readData: () => Promise<Uint8Array>
        ) => {
            const takes = () => {
                const tab = stateRef.current.tabs.find(
                    (item) => item.id === id
                );
                return mountedRef.current &&
                    tab?.status === "ready" &&
                    tab.selectedSource?.document_url === documentUrl &&
                    !heldPdfsRef.current.has(id)
                    ? tab
                    : null;
            };
            if (!takes()) return;
            void readData().then(
                (data) => {
                    const tab = takes();
                    if (!tab) return;
                    heldPdfsRef.current.set(id, {
                        documentId: tab.documentId,
                        ...documentInfoOptions(tab.variant),
                        url: URL.createObjectURL(
                            new Blob([data.slice()], {
                                type: "application/pdf",
                            })
                        ),
                    });
                },
                () => undefined
            );
        },
        []
    );

    const setTabPage = useCallback((id: string, page: number) => {
        const current = stateRef.current;
        if (
            id !== current.activeTabId ||
            !Number.isInteger(page) ||
            page < 1 ||
            current.tabs.find((tab) => tab.id === id)?.currentPage === page
        )
            return;
        stateRef.current = {
            ...current,
            tabs: current.tabs.map((tab) =>
                tab.id === id ? { ...tab, currentPage: page } : tab
            ),
        };
    }, []);

    useEffect(() => {
        const jump = pendingJumpRef.current;
        if (jump && jump.tabId === stateRef.current.activeTabId) {
            jumpToPageRef.current?.(jump.page);
        }
        pendingJumpRef.current = null;
    }, [jumpSeq]);

    const previousConversationRef = useRef({
        conversationId,
        threadId: mainThreadId,
        threadIsEstablished: mainThreadIsEstablished,
    });
    useEffect(() => {
        const previous = previousConversationRef.current;
        previousConversationRef.current = {
            conversationId,
            threadId: mainThreadId,
            threadIsEstablished: mainThreadIsEstablished,
        };
        const routeChanged = previous.conversationId !== conversationId;
        const establishedThreadChanged =
            previous.threadId != null &&
            previous.threadIsEstablished &&
            previous.threadId !== mainThreadId;
        if (routeChanged || establishedThreadChanged) closeDocument();
    }, [conversationId, mainThreadId, mainThreadIsEstablished, closeDocument]);

    const value = useMemo(() => {
        return {
            ...state,
            ...projectActiveTab(state),
            openDocument,
            openDocumentById,
            activateTab,
            closeTab,
            setTabPage,
            holdTabPdf,
            closeDocument,
            jumpToPageRef,
        };
    }, [
        state,
        openDocument,
        openDocumentById,
        activateTab,
        closeTab,
        setTabPage,
        holdTabPdf,
        closeDocument,
    ]);

    return (
        <DocumentViewerContext.Provider value={value}>
            {children}
        </DocumentViewerContext.Provider>
    );
};
